import { motion } from 'framer-motion';
import { Calculator, Truck, Package, Wrench, ArrowLeftRight, Tag } from 'lucide-react';
import { useProposalData } from '../../context';
import { Section, SectionHeader } from '../layout';
import { PriceSummaryRow } from '../common';
import { formatCurrency } from '../../utils';
import { WithFeedback } from '../feedback';

export function PricingSection() {
  const proposal = useProposalData();
  const { pricing } = proposal;

  // Calculate totals ourselves - don't trust pricing.subtotal/total from JSON
  // because they may include optionsSubtotal which is already in equipmentSubtotal
  const calculatedSubtotal = pricing.equipmentSubtotal + pricing.addonsSubtotal + pricing.servicesSubtotal;
  const calculatedSavings = pricing.tradeInCredit + pricing.discounts;
  const calculatedTotal = calculatedSubtotal - calculatedSavings;

  return (
    <Section id="pricing" className="bg-gray-50">
      <SectionHeader
        title="PRICING SUMMARY"
        subtitle="Investment"
      />

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Line Item Breakdown */}
        <motion.div
          className="lg:col-span-2"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <WithFeedback section="pricing" position="top-right">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              {/* Equipment Section */}
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-hul-navy/10 rounded-lg flex items-center justify-center">
                    <Truck className="w-5 h-5 text-hul-navy" />
                  </div>
                  <h3 className="font-heading text-lg uppercase tracking-wider text-hul-navy">
                    Equipment
                  </h3>
                </div>

                <div className="space-y-2 pl-13">
                  <PriceSummaryRow
                    label={`Equipment (${proposal.equipment.length} lines)`}
                    amount={pricing.equipmentSubtotal}
                  />
                  {/* Options are already included in equipmentSubtotal */}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
                  <span className="font-medium text-gray-700">Equipment Subtotal</span>
                  <span className="font-mono text-xl font-bold text-hul-navy">
                    {formatCurrency(pricing.equipmentSubtotal)}
                  </span>
                </div>
              </div>

              {/* Add-ons Section */}
              {pricing.addonsSubtotal > 0 && (
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Package className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="font-heading text-lg uppercase tracking-wider text-hul-navy">
                      Add-ons & Accessories
                    </h3>
                  </div>

                  <div className="pl-13">
                    <PriceSummaryRow
                      label="Attachments, Technology, Installation"
                      amount={pricing.addonsSubtotal}
                    />
                  </div>
                </div>
              )}

              {/* Services Section */}
              {pricing.servicesSubtotal > 0 && (
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Wrench className="w-5 h-5 text-purple-600" />
                    </div>
                    <h3 className="font-heading text-lg uppercase tracking-wider text-hul-navy">
                      Services
                    </h3>
                  </div>

                  <div className="pl-13">
                    <PriceSummaryRow
                      label="Training & Support Services"
                      amount={pricing.servicesSubtotal}
                    />
                  </div>
                </div>
              )}

              {/* Subtotal */}
              <div className="p-6 bg-gray-50 border-b border-gray-100">
                <div className="flex justify-between items-center">
                  <span className="font-heading text-lg text-gray-700">Subtotal</span>
                  <span className="font-mono text-2xl font-bold text-hul-navy">
                    {formatCurrency(calculatedSubtotal)}
                  </span>
                </div>
              </div>

              {/* Credits Section */}
              {(pricing.tradeInCredit > 0 || pricing.discounts > 0) && (
                <div className="p-6 bg-green-50 border-b border-green-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Tag className="w-5 h-5 text-green-600" />
                    </div>
                    <h3 className="font-heading text-lg uppercase tracking-wider text-green-700">
                      Credits & Savings
                    </h3>
                  </div>

                  <div className="space-y-2 pl-13">
                    {pricing.tradeInCredit > 0 && (
                      <div className="flex items-center justify-between text-green-700">
                        <span className="flex items-center gap-2">
                          <ArrowLeftRight className="w-4 h-4" />
                          Trade-In Credit
                        </span>
                        <span className="font-mono font-medium">
                          -{formatCurrency(pricing.tradeInCredit)}
                        </span>
                      </div>
                    )}
                    {pricing.discounts > 0 && (
                      <div className="flex items-center justify-between text-green-700">
                        <span className="flex items-center gap-2">
                          <Tag className="w-4 h-4" />
                          Discounts & Rebates
                        </span>
                        <span className="font-mono font-medium">
                          -{formatCurrency(pricing.discounts)}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-green-200 flex justify-between items-center">
                    <span className="font-medium text-green-700">Total Savings</span>
                    <span className="font-mono text-xl font-bold text-green-700">
                      -{formatCurrency(pricing.tradeInCredit + pricing.discounts)}
                    </span>
                  </div>
                </div>
              )}

              {/* Grand Total */}
              <div className="p-4 sm:p-8 bg-hul-navy text-white">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                  <div>
                    <div className="text-white/70 text-xs sm:text-sm uppercase tracking-wider mb-1">
                      Proposal Total
                    </div>
                    <div className="font-display text-lg sm:text-2xl tracking-wide">
                      INVESTMENT TOTAL
                    </div>
                  </div>
                  <div className="text-left sm:text-right">
                    <div className="font-mono text-3xl sm:text-4xl lg:text-5xl font-bold text-white break-words">
                      {formatCurrency(calculatedTotal)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </WithFeedback>
        </motion.div>

        {/* Summary Card */}
        <motion.div
          className="lg:col-span-1"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          <div className="sticky top-24 space-y-6">
            {/* Quick Summary */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <Calculator className="w-6 h-6 text-hul-red" />
                <h3 className="font-heading text-lg uppercase tracking-wider text-hul-navy">
                  Quick Summary
                </h3>
              </div>

              <div className="space-y-4">
                <SummaryItem
                  label="Equipment"
                  value={pricing.equipmentSubtotal}
                  icon={<Truck className="w-4 h-4" />}
                />
                {pricing.addonsSubtotal > 0 && (
                  <SummaryItem
                    label="Add-ons"
                    value={pricing.addonsSubtotal}
                    icon={<Package className="w-4 h-4" />}
                  />
                )}
                {pricing.servicesSubtotal > 0 && (
                  <SummaryItem
                    label="Services"
                    value={pricing.servicesSubtotal}
                    icon={<Wrench className="w-4 h-4" />}
                  />
                )}

                <div className="h-px bg-gray-200" />

                {(pricing.tradeInCredit > 0 || pricing.discounts > 0) && (
                  <>
                    <SummaryItem
                      label="Savings"
                      value={-(pricing.tradeInCredit + pricing.discounts)}
                      icon={<Tag className="w-4 h-4" />}
                      isCredit
                    />
                    <div className="h-px bg-gray-200" />
                  </>
                )}

                <div className="flex justify-between items-center pt-2">
                  <span className="font-heading text-hul-navy">Total</span>
                  <span className="font-mono text-2xl font-bold text-hul-red">
                    {formatCurrency(calculatedTotal)}
                  </span>
                </div>
              </div>
            </div>

            {/* Financing Preview - Hidden for Cash deals */}
            {proposal.financing?.type !== 'Cash' && proposal.financing?.monthlyPayment && (
              <div className="bg-gradient-to-br from-hul-red to-hul-red-dark rounded-xl p-6 text-white">
                <div className="text-white/80 text-sm mb-1">or starting at</div>
                <div className="font-mono text-3xl font-bold">
                  {formatCurrency(proposal.financing.monthlyPayment)}
                  <span className="text-lg font-normal">/mo</span>
                </div>
                <div className="text-white/80 text-sm mt-1">
                  with {proposal.financing.type.toLowerCase()}
                </div>
                <a
                  href="#financing"
                  className="inline-block mt-4 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm transition-colors"
                >
                  View Financing Options
                </a>
              </div>
            )}

            {/* Valid Until */}
            <div className="text-center text-sm text-gray-500">
              Pricing valid until{' '}
              <span className="font-medium text-hul-navy">
                {new Date(proposal.proposal.validUntil).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </Section>
  );
}

interface SummaryItemProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  isCredit?: boolean;
}

function SummaryItem({ label, value, icon, isCredit }: SummaryItemProps) {
  return (
    <div className="flex items-center justify-between">
      <span className={`flex items-center gap-2 ${isCredit ? 'text-green-600' : 'text-gray-600'}`}>
        {icon}
        {label}
      </span>
      <span className={`font-mono font-medium ${isCredit ? 'text-green-600' : 'text-gray-900'}`}>
        {isCredit && value < 0 ? '' : ''}
        {formatCurrency(Math.abs(value))}
      </span>
    </div>
  );
}
